import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Copy, Send, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Footer } from "@/components/Footer";
import { submitContactForm } from "@/lib/api";

export default function Press() {
  useEffect(() => { document.title = "ROADRIG · Press & Founder Kit — Coming Soon"; }, []);

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContactForm({ ...form, source: "press" });
      setSent(true);
      toast.success("Message sent — I'll be in touch.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again or email hello@roadrig.com.au.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label}.`); }
    catch { toast.error("Copy failed."); }
  };

  return (
    <div data-testid="press-page" className="relative bg-[#0A0A0C] text-white min-h-screen">
      {/* Header strip */}
      <header className="py-8 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between">
          <Link to="/" data-testid="press-logo-link" className="h-display text-2xl">
            ROAD<span className="text-[#F47A20]">RIG</span>
          </Link>
          <Link to="/" data-testid="press-back-link" className="serial text-[10px] text-white/60 hover:text-white flex items-center gap-2">
            <ArrowLeft size={12} /> Back to Site
          </Link>
        </div>
      </header>

      {/* Coming Soon */}
      <section className="py-24 md:py-36" data-testid="press-coming-soon">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="section-mark" />
              <span className="overline">Press & Founder Kit</span>
              <span className="section-mark" />
            </div>
            <h1 className="h-display text-white text-5xl sm:text-6xl lg:text-7xl" data-testid="press-coming-soon-heading">
              Coming <span className="text-[#F47A20]">Soon.</span>
            </h1>
            <p className="mt-8 text-white/75 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              We&rsquo;re still building the ROADRIG story.
            </p>
            <p className="mt-3 text-white/60 text-base max-w-xl mx-auto leading-relaxed">
              Our media kit, downloadable assets and press resources will be available closer to launch.
            </p>
            <div className="mt-10 inline-flex items-center gap-3 px-5 py-4 glass rounded-sm">
              <Clock size={16} className="text-[#F47A20]" strokeWidth={1.6} />
              <span className="serial text-[10px] text-white/70">
                MEDIA ENQUIRIES ·&nbsp;
                <a href="mailto:hello@roadrig.com.au?subject=ROADRIG%20media%20enquiry" data-testid="press-email-link" className="text-[#F47A20] hover:underline normal-case tracking-normal text-xs font-bold">
                  hello@roadrig.com.au
                </a>
              </span>
              <button type="button" onClick={() => copy("hello@roadrig.com.au", "Press email copied")} className="inline-flex items-center gap-1 text-[#F47A20] hover:underline" data-testid="copy-press-email">
                <Copy size={11} />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 border-t border-white/5 bg-[#0d0d10]" data-testid="press-contact">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="section-mark" />
              <span className="overline">Contact</span>
              <span className="section-mark" />
            </div>
            <h2 className="h-display text-white text-4xl md:text-5xl mb-6">Talk to John directly.</h2>
            <p className="text-white/65 max-w-xl mx-auto mb-10">
              For interviews, sample requests, or any story you want to tell about ROADRIG, the fastest way through is straight to the founder.
            </p>
          </div>

          {sent ? (
            <motion.div
              data-testid="contact-success"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#F47A20]/40 bg-[#F47A20]/5 rounded-sm p-8 md:p-10 text-center"
            >
              <div className="overline text-[#F47A20] mb-3">Message received</div>
              <h3 className="h-display text-white text-2xl md:text-3xl mb-3">You&rsquo;ll hear back soon.</h3>
              <p className="text-white/70 mb-6">A confirmation is on its way to your inbox. Reply to it any time — it comes straight to John.</p>
              <button onClick={() => setSent(false)} className="btn-secondary" data-testid="contact-send-another">
                Send another message
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submitContact} className="grid gap-4 md:gap-5" data-testid="contact-form" noValidate>
              <div className="grid md:grid-cols-2 gap-4 md:gap-5">
                <label className="block">
                  <span className="overline text-white/60 mb-2 inline-block">Your name</span>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Jane Rider"
                    data-testid="contact-input-name"
                    className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="overline text-white/60 mb-2 inline-block">Email</span>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@example.com"
                    data-testid="contact-input-email"
                    className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors"
                  />
                </label>
              </div>
              <label className="block">
                <span className="overline text-white/60 mb-2 inline-block">Subject</span>
                <input
                  type="text"
                  value={form.subject}
                  onChange={onChange("subject")}
                  placeholder="Press enquiry / interview / sample request"
                  data-testid="contact-input-subject"
                  className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors"
                />
              </label>
              <label className="block">
                <span className="overline text-white/60 mb-2 inline-block">Message</span>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={onChange("message")}
                  placeholder="Tell John what you're working on..."
                  data-testid="contact-input-message"
                  className="w-full bg-transparent border border-white/15 focus:border-[#F47A20] outline-none text-white placeholder:text-white/30 px-4 py-3 rounded-sm transition-colors resize-y"
                />
              </label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="serial text-[10px] text-white/40">
                  Or email us directly:&nbsp;
                  <a href="mailto:hello@roadrig.com.au?subject=ROADRIG%20press%20enquiry" data-testid="press-email-link-form" className="text-[#F47A20] hover:underline">
                    hello@roadrig.com.au
                  </a>
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary min-w-[180px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending&hellip;
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Send message
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
